import { FieldListeners, IField, IFieldData } from "./FieldListener";
import {
  clearChars,
  modifiers,
  parseCompletedChar,
  skipChars,
} from "./constants";

export type ParseMode = "ai" | "value";
export interface ParsedGS1Data {
  GTIN?: string;
  LOT?: string;
  EXP?: string;
  SN?: string;
}

type GS1Key = keyof ParsedGS1Data;
type Gs1Ai = "01" | "10" | "17" | "21";
type AiSpec =
  | { kind: "fixed"; len: number; key: GS1Key }
  | { kind: "var"; key: GS1Key };
type AiMap = Record<Gs1Ai, AiSpec>;
type Score = { fields: number; consumed: number; valueChars: number };
type ParseResult = { data: ParsedGS1Data; pos: number; score: Score };

export class Parser {
  private readonly modifiers: string[] = modifiers;
  private readonly skipChars: string[] = skipChars;
  private readonly parseCompletedChar: string = parseCompletedChar;
  private readonly clearChars: string[] = clearChars;
  private isParserExited: boolean = false;
  private parseMode: ParseMode = "ai";
  private subscribers: FieldListeners[] = [];
  private targetSubscriber: FieldListeners | undefined;
  constructor(fields: IField[]) {
    this.subscribers = fields.map(
      (field) => new FieldListeners(this, field.identifier, field?.size)
    );
  }
  public parseChar(char: string): void {
    if (this.isChar(char, this.parseCompletedChar)) {
      this.isParserExited = true;
      return;
    }
    if (this.isCharIncluded(char, [...this.modifiers, ...this.skipChars]))
      return;
    if (this.targetSubscriber) {
      this.targetSubscriber.streamChars(char);
      return;
    }
    this.publish(char);
  }

  public parseRawGS1(rawInput: string): ParsedGS1Data {
    return this.parseGS1Smart(rawInput);
  }

  // GS1 tokenizer for supported AIs with safe variable-length handling.
  public parseGS1Smart(rawInput: string): ParsedGS1Data {
    const raw = rawInput.trim();
    if (!raw) return {};

    const AIs = this.getAiMap();
    const aiKeys = this.getAiKeys(AIs);

    const startsWithKnownAI = aiKeys.some((ai) => raw.startsWith(ai));
    if (startsWithKnownAI) {
      return this.parseAt(raw, 0, {}, AIs, aiKeys).data;
    }

    const idx01 = raw.indexOf("01");
    if (idx01 >= 0) return this.parseAt(raw, idx01, {}, AIs, aiKeys).data;

    return {};
  }

  private getAiMap(): AiMap {
    return {
      "01": { kind: "fixed", len: 14, key: "GTIN" },
      "17": { kind: "fixed", len: 6, key: "EXP" },
      "10": { kind: "var", key: "LOT" },
      "21": { kind: "var", key: "SN" },
    };
  }

  private getAiKeys(AIs: AiMap): Gs1Ai[] {
    return Object.keys(AIs) as Gs1Ai[];
  }

  private isGroupSepChar(c: string): boolean {
    return c === "\u001d" || c === "^" || c.toUpperCase() === "F8";
  }

  private isValidValueChar(c: string): boolean {
    return /^[a-zA-Z0-9-]$/.test(c);
  }

  private isDigits(s: string): boolean {
    return /^\d+$/.test(s);
  }

  private getScore(seen: ParsedGS1Data, consumed: number): Score {
    const fields = Object.values(seen).filter(Boolean).length;
    const valueChars = Object.values(seen).reduce((sum, v) => sum + (v ? v.length : 0), 0);
    return { fields, consumed, valueChars };
  }

  private better(a: ParseResult, b: ParseResult): boolean {
    if (a.score.fields !== b.score.fields) return a.score.fields > b.score.fields;
    if (a.score.consumed !== b.score.consumed) return a.score.consumed > b.score.consumed;
    return a.score.valueChars > b.score.valueChars;
  }

  private merge(base: ParsedGS1Data, key: GS1Key, value: string): ParsedGS1Data {
    if (!value) return base;
    if (base[key]) return base;
    return { ...base, [key]: value };
  }

  private getCutCandidates(raw: string, pos: number, aiKeys: Gs1Ai[]): number[] {
    for (let j = pos + 2; j < raw.length; j++) {
      if (this.isGroupSepChar(raw[j])) return [j];
    }

    const cuts: number[] = [];
    for (let j = pos + 2; j + 2 <= raw.length; j++) {
      const next = raw.substring(j, j + 2) as Gs1Ai;
      if (aiKeys.includes(next)) cuts.push(j);
    }
    cuts.push(raw.length);

    return Array.from(new Set(cuts)).filter((j) => j > pos + 2);
  }

  private parseAt(raw: string, startPos: number, seen: ParsedGS1Data, AIs: AiMap, aiKeys: Gs1Ai[]): ParseResult {
    let best: ParseResult = { data: seen, pos: startPos, score: this.getScore(seen, startPos) };

    let pos = startPos;
    while (pos < raw.length) {
      if (this.isGroupSepChar(raw[pos])) {
        pos += 1;
        continue;
      }

      const ai = raw.substring(pos, pos + 2) as Gs1Ai;
      if (!(ai in AIs)) {
        const candidate: ParseResult = { data: seen, pos, score: this.getScore(seen, pos) };
        return this.better(best, candidate) ? best : candidate;
      }

      const spec = AIs[ai];
      if (spec.kind === "fixed") {
        const value = raw.substring(pos + 2, pos + 2 + spec.len);
        if (value.length !== spec.len || !this.isDigits(value)) return best;

        seen = this.merge(seen, spec.key, value);
        pos = pos + 2 + spec.len;
        best = { data: seen, pos, score: this.getScore(seen, pos) };
        continue;
      }

      const cutCandidates = this.getCutCandidates(raw, pos, aiKeys);

      let localBest: ParseResult | undefined;
      for (const cut of cutCandidates) {
        const value = raw.substring(pos + 2, cut);
        if (!value) continue;
        if (!value.split("").every((c) => this.isValidValueChar(c))) continue;

        const nextPos = cut < raw.length && this.isGroupSepChar(raw[cut]) ? cut + 1 : cut;
        const nextSeen = this.merge(seen, spec.key, value);

        const branch = this.parseAt(raw, nextPos, nextSeen, AIs, aiKeys);
        if (!localBest || this.better(branch, localBest)) localBest = branch;
      }

      return localBest ?? best;
    }

    return best;
  }

  public resetParser(): void {
    this.isParserExited = false;
    this.removeTargetSubscriber();
    this.hardResetSubscribers();
  }

  public quickScan(char: string): void {
    if (!this.isChar(char, this.parseCompletedChar)) return;
    this.isParserExited = true;
  }

  public getParseMode() {
    return this.parseMode;
  }

  public removeTargetSubscriber() {
    this.parseMode = "ai";
    this.targetSubscriber = undefined;
  }
  public setTargetSubscriber(subscriber: FieldListeners) {
    this.resetFields();
    this.targetSubscriber = subscriber;
    this.parseMode = "value";
  }

  public shouldParserExit(): boolean {
    return this.isParserExited;
  }

  public getFields(): IFieldData[] {
    return this.subscribers.map((subscriber) => subscriber.getData());
  }

  private isChar(char: string, ref: string): boolean {
    return char.toLowerCase() === ref.toLowerCase();
  }

  private isCharIncluded(char: string, refs: string[]): boolean {
    return refs.includes(char.toLowerCase());
  }

  private publish(char: string) {
    for (let i = 0, n = this.subscribers.length; i < n; i++) {
      const isMatched = this.subscribers[i].streamChars(char);
      if (isMatched) break;
    }
  }

  private resetFields() {
    this.subscribers.forEach((subscriber) => subscriber.clearAIField());
  }

  public hardResetSubscribers() {
    this.subscribers.forEach((subscriber) => subscriber.forceClear());
  }
}
